import { create } from "zustand";
import { apiFetch } from "@/lib/api";

export type AutomationType =
  | "auto_reply"
  | "welcome"
  | "keyword"
  | "follow_up"
  | "event"
  | "scheduled"
  | "feedback";

export type AutomationScope = "private" | "group" | "channel" | "all";
export type KeywordMatchMode = "exact" | "contains" | "regex";
export type ActionType = "send_message" | "notify_owner" | "add_label";
export type TemplateCategory = "engagement" | "support" | "sales" | "operations" | "general" | "retail";
export type ActionLanguage = "auto" | "en" | "am" | "auto_detect";

export interface TriggerConfig {
  type: "message" | "keyword" | "schedule" | "event" | "new_contact" | "no_reply";
  keywords?: string[];
  keywordMatchMode?: KeywordMatchMode;
  eventName?: string;
  delayMinutes?: number;
}

export interface ActionConfig {
  type: ActionType;
  message?: string;
  messageAmharic?: string;
  language?: ActionLanguage;
  aiEnabled?: boolean;
  variables?: string[] | Record<string, string>;
}

export interface ScheduleConfig {
  activeHours?: { start: string; end: string };
  activeDays?: string[];
  timezone?: string;
  outsideHoursOnly?: boolean;
}

export interface Automation {
  id: number;
  name: string;
  description: string;
  automationType: AutomationType;
  enabled: boolean;
  priority: number;
  scope: AutomationScope;
  targetCategories: string[];
  triggerConfig: TriggerConfig;
  actionConfig: ActionConfig;
  scheduleConfig: ScheduleConfig;
  statsTriggered: number;
  statsLastTriggered: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AutomationTemplate {
  id: number;
  name: string;
  nameAm: string;
  description: string;
  descriptionAm: string;
  automationType: AutomationType;
  triggerConfig: TriggerConfig;
  actionConfig: ActionConfig;
  scheduleConfig: ScheduleConfig;
  category: TemplateCategory;
}

interface AutomationState {
  automations: Automation[];
  templates: AutomationTemplate[];
  selectedAutomation: Automation | null;
  isLoading: boolean;
}

interface AutomationActions {
  loadAutomations: () => Promise<void>;
  loadTemplates: () => Promise<void>;
  createAutomation: (input: CreateAutomationInput) => Promise<void>;
  updateAutomation: (id: number, updates: Partial<Automation>) => Promise<void>;
  deleteAutomation: (id: number) => Promise<void>;
  toggleAutomation: (id: number) => Promise<void>;
  selectAutomation: (automation: Automation | null) => void;
  createFromTemplate: (templateId: number) => Promise<void>;
}

type CreateAutomationInput = Omit<
  Automation,
  "id" | "statsTriggered" | "statsLastTriggered" | "createdAt" | "updatedAt"
>;

interface AutomationsResponse {
  automations: ApiAutomation[];
}

interface TemplatesResponse {
  templates: ApiAutomationTemplate[];
}

interface ApiAutomation {
  id: number;
  name: string;
  description: string | null;
  automationType: AutomationType;
  enabled: boolean;
  priority: number;
  scope: AutomationScope;
  targetCategories: string[];
  triggerConfig: TriggerConfig;
  actionConfig: ActionConfig;
  scheduleConfig: ScheduleConfig | null;
  statsTriggered: number;
  statsLastTriggered: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ApiAutomationTemplate {
  id: number;
  name: string;
  nameAm: string | null;
  description: string | null;
  descriptionAm: string | null;
  automationType: AutomationType;
  triggerConfig: TriggerConfig;
  actionConfig: ActionConfig;
  scheduleConfig: ScheduleConfig | null;
  category: TemplateCategory;
}

function mapAutomation(automation: ApiAutomation): Automation {
  return {
    id: automation.id,
    name: automation.name,
    description: automation.description ?? "",
    automationType: automation.automationType,
    enabled: automation.enabled,
    priority: automation.priority,
    scope: automation.scope,
    targetCategories: automation.targetCategories,
    triggerConfig: automation.triggerConfig,
    actionConfig: automation.actionConfig,
    scheduleConfig: automation.scheduleConfig ?? {},
    statsTriggered: automation.statsTriggered,
    statsLastTriggered: automation.statsLastTriggered,
    createdAt: automation.createdAt,
    updatedAt: automation.updatedAt,
  };
}

function mapTemplate(template: ApiAutomationTemplate): AutomationTemplate {
  return {
    id: template.id,
    name: template.name,
    nameAm: template.nameAm ?? "",
    description: template.description ?? "",
    descriptionAm: template.descriptionAm ?? "",
    automationType: template.automationType,
    triggerConfig: template.triggerConfig,
    actionConfig: template.actionConfig,
    scheduleConfig: template.scheduleConfig ?? {},
    category: template.category,
  };
}

function toApiInput(input: CreateAutomationInput): Record<string, unknown> {
  return {
    name: input.name,
    description: input.description,
    automationType: input.automationType,
    enabled: input.enabled,
    priority: input.priority,
    scope: input.scope,
    targetCategories: input.targetCategories,
    triggerConfig: input.triggerConfig,
    actionConfig: input.actionConfig,
    scheduleConfig: input.scheduleConfig,
  };
}

export const useAutomationStore = create<AutomationState & AutomationActions>((set, get) => ({
  automations: [],
  templates: [],
  selectedAutomation: null,
  isLoading: false,

  loadAutomations: async () => {
    set({ isLoading: true });
    try {
      const response = await apiFetch<AutomationsResponse>("/api/automations");
      set({
        automations: response.automations.map(mapAutomation),
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  loadTemplates: async () => {
    const response = await apiFetch<TemplatesResponse>("/api/automations/templates");
    set({ templates: response.templates.map(mapTemplate) });
  },

  createAutomation: async (input) => {
    const createdAutomation = await apiFetch<ApiAutomation>("/api/automations", {
      method: "POST",
      body: JSON.stringify(toApiInput(input)),
    });
    set({ automations: [...get().automations, mapAutomation(createdAutomation)] });
  },

  updateAutomation: async (id, updates) => {
    const updatedAutomation = await apiFetch<ApiAutomation>(`/api/automations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
    set({
      automations: get().automations.map((automation) =>
        automation.id === id ? mapAutomation(updatedAutomation) : automation,
      ),
    });
  },

  deleteAutomation: async (id) => {
    await apiFetch(`/api/automations/${id}`, { method: "DELETE" });
    set({
      automations: get().automations.filter((automation) => automation.id !== id),
      selectedAutomation: null,
    });
  },

  toggleAutomation: async (id) => {
    const existingAutomation = get().automations.find((automation) => automation.id === id);
    if (!existingAutomation) return;

    const updatedAutomation = await apiFetch<ApiAutomation>(`/api/automations/${id}/toggle`, {
      method: "PATCH",
      body: JSON.stringify({ enabled: !existingAutomation.enabled }),
    });
    set({
      automations: get().automations.map((automation) =>
        automation.id === id ? mapAutomation(updatedAutomation) : automation,
      ),
    });
  },

  selectAutomation: (automation) => {
    set({ selectedAutomation: automation });
  },

  createFromTemplate: async (templateId) => {
    const createdAutomation = await apiFetch<ApiAutomation>(
      `/api/automations/from-template/${templateId}`,
      {
        method: "POST",
        body: JSON.stringify({}),
      },
    );
    set({ automations: [...get().automations, mapAutomation(createdAutomation)] });
  },
}));
